<svelte:options
	customElement={{
		extend: (customElementConstructor) => {
			return class extends customElementConstructor {
				/**
				 * The Svelte way to determine the host element
				 * @see {@link https://github.com/sveltejs/svelte/issues/3091#issuecomment-1642351615}
				 */
				constructor() {
					super();
					// @ts-expect-error Research how to determine the correct type
					this.host = this;
				}
			};
		},
		tag: 'dst-header',
	}}
/>

<script lang="ts">
	import { baseUrls } from '@/config.ts';
	import '@/global.d.ts';
	import { brandingLabels } from '@/labels.ts';
	import { changeState, register as registerClosables } from '@/lib/closable.ts';
	import { Logger } from '@/lib/log.ts';
	import { closableInformElement } from '@/stores/header_store.ts';
	import { loginState } from '@derstandard/login-state';
	import {
		dstAppIcon,
		dstBrandLogo,
		dstHeaderChevronDown,
		dstHeaderMenu,
		dstHeaderMenuSearch,
		dstHeaderMenuSearchVariant,
		dstHeaderMenuVariant,
	} from '@derstandard/styling-toolkit/src/svg/lib.js';
	import { onMount } from 'svelte';
	import { writable } from 'svelte/store';

	const log = Logger('header.DstHeader');

	// web components
	import '../DstFlyout/DstFlyout.webcomponent.svelte';
	import '../DstNotificationsWrapper/DstNotificationsWrapper.webcomponent.svelte';
	import '../DstPromotion/DstPromotion.webcomponent.svelte';
	import '../DstUseractions/DstUseractions.webcomponent.svelte';

	import type { CognitoGroups } from '@/global.d.ts';

	// svelte components
	import { onResizeNavigation } from '@/lib/navigation.ts';

	import Navigation from '../Navigation/Navigation.svelte';
	import Sidebar from '../Sidebar/Sidebar.svelte';

	// props
	export let appid;
	export let variant;
	export let hint;

	$: isMinimal = hint === 'minimal';
	$: gestalt = `${appid}_${variant}${isMinimal ? '_minimal' : ''}`;
	$: baseUrl = baseUrls[`${appid}_${variant}`] ?? '/';
	$: isLegalVariant = Object.keys(baseUrls).indexOf(gestalt) !== -1;
	$: hasHeroGestalt = gestalt === 'editorialweb_at' || gestalt === 'livecontent_at';
	$: hasHero = $$slots.hero || hasHeroGestalt;
	$: isPurUser = ($loginState.payload?.['cognito:groups'] as CognitoGroups)?.includes('pur') || false;

	const isCompactNav = writable<boolean>(false);

	const isLiveContent = window.DERSTANDARD?.pageConfig?.pageType === 'liveticker';
	const isCommunityfeedContent = window.DERSTANDARD?.pageConfig?.pageType === 'communityFeed';
	const isUserprofileContent = window.DERSTANDARD?.pageConfig?.pageType === 'userprofile';
	const brandLogoAppIds = ['editorialweb', 'livecontent', 'userprofile', 'communityfeed'];
	const appIconAppIds = ['immobilien', 'jobs'];

	const notificationsDisabled = Boolean(window.DERSTANDARD?.pageConfig?.settings?.disableNotifications);

	export let host;

	let root;
	let brandCssClass;
	let brandIcon;
	let brandLabelCssClass;
	let brandLabelText;
	let brandUrl;
	let PushNotifications;

	/**
	 * Handle page scroll
	 */
	let scrollHandlerDebounce = false;
	let lastScrollPosition = 0;
	const scrollTolerance = 8;

	/**
	 * Handle scrolling events
	 */
	function handleScroll() {
		if (scrollHandlerDebounce || isLiveContent || isCommunityfeedContent || isUserprofileContent) {
			return;
		}
		scrollHandlerDebounce = true;
		toggleCompact(false);

		setTimeout(() => {
			scrollHandlerDebounce = false;
		}, 10);
	}

	/**
	 * Toggle normal and compact header
	 * @param initial
	 */
	function toggleCompact(initial: boolean) {
		if (isMinimal || !host) {
			return;
		}

		const scrollTop = window.scrollY || document.documentElement.scrollTop;
		const scrolled = scrollTop > (host.classList.contains('compact--visible') ? 0 : 150);
		const scrolledThreshold = scrollTop > host.clientHeight;
		const scrolledUp = scrollTop + scrollTolerance < lastScrollPosition;
		const scrolledDown = scrollTop - scrollTolerance > lastScrollPosition;

		// Check if a closable is active
		const activeClosable = Boolean(host.dataset.activeClosable);

		if (scrolled) {
			if (scrolledThreshold) {
				isCompactNav.set(true);
				host.classList.add('compact');

				setTimeout(() => {
					host.classList.add('compact--transition');
				}, 50);

				if (scrolledUp || activeClosable || initial) {
					host.classList.add('compact--visible');
				} else if (scrolledDown && !activeClosable) {
					host.classList.remove('compact--visible');
				}
			} else {
				host.classList.remove('compact--transition');
			}
		} else {
			isCompactNav.set(false);
			host.classList.remove('compact', 'compact--visible', 'compact--transition');
		}

		setTimeout(() => {
			if (onResizeNavigation) {
				onResizeNavigation();
			}
		}, 50);

		lastScrollPosition = scrollTop;
	}

	/**
	 * Shift focus to target element
	 * @param selector
	 * @param customElement
	 */
	function shiftFocusTo(selector: string, customElement?: string) {
		log.debug(`Shift focus to ${selector}.`);
		const selectionRoot = customElement ? root.querySelector(customElement).shadowRoot : root;
		const target = selectionRoot.querySelector(selector);

		target.focus();
	}

	/**
	 * Open/focus visible or collapsed sidebar
	 * @param sidebarSelector
	 * @param targetSelector
	 */
	function focusSidebar(sidebarSelector, targetSelector): void {
		if (!sidebarSelector || !targetSelector) {
			return;
		}
		const sidebar = root.querySelector(sidebarSelector);
		const isVisible = window.getComputedStyle(sidebar).display !== 'none';
		const target = sidebar?.shadowRoot?.querySelector(targetSelector);

		if (isVisible) {
			shiftFocusTo(targetSelector, sidebarSelector);
		} else {
			changeState(sidebar, true, target);
		}
	}

	/**
	 * Handle click of a jump label
	 * @param e
	 */
	function onJumpLableClick(e): void {
		const hash = e?.target?.hash;

		switch (true) {
			case hash === '#app-sidebar':
				focusSidebar('.app--sidebar', 'a');
				break;

			case hash === '#app-selectapp':
				shiftFocusTo('.app--header--selectapp button');
				break;

			case hash === '#app-useractions':
				shiftFocusTo('.dst-useractions > button', 'dst-useractions');
				break;
		}
	}

	/**
	 * Handle click of mobile sidebar toggle
	 */
	function onSidebarToggleClick(): void {
		focusSidebar('.app--sidebar', 'a');
	}

	onMount(async () => {
		// Add ID for jump marks to main. This is not doable with JS focus().
		const main = document.querySelector('main');

		if (!main?.getAttribute('id')) {
			main?.setAttribute('id', 'main');
		}

		log.debug(`Created final header form: ${gestalt} from`, $$props);
		closableInformElement.set(host);
		registerClosables(root, $closableInformElement as HTMLElement);
		brandCssClass =
			brandLogoAppIds.indexOf(appid) !== -1
				? 'brandlogo'
				: appIconAppIds.indexOf(appid) !== -1
					? `appicon appicon--${appid}`
					: 'brandicon';
		brandIcon =
			brandLogoAppIds.indexOf(appid) !== -1
				? 'brandlogo'
				: appIconAppIds.indexOf(appid) !== -1
					? 'appicon'
					: 'brandicon';
		brandLabelCssClass =
			brandLogoAppIds.indexOf(appid) !== -1
				? 'sr-only'
				: appIconAppIds.indexOf(appid) !== -1
					? 'appicon--label'
					: 'brandicon--label';
		brandLabelText = brandingLabels[appid] || brandingLabels.editorialweb;
		brandUrl = appid === 'abo' ? baseUrls.abo_default : baseUrl;

		if (!(scrollHandlerDebounce || isLiveContent || isCommunityfeedContent || isUserprofileContent)) {
			toggleCompact(true);
		}

		if (!isPurUser && appid === 'editorialweb' && variant === 'at' && hint !== 'minimal') {
			PushNotifications = (await import('../PushNotifications/PushNotifications.svelte')).default;
		}
	});
</script>

<svelte:window on:scroll|passive={!isMinimal && handleScroll} />

<header class="app--header" bind:this={root}>
	<svg xmlns="http://www.w3.org/2000/svg" style="display: none;">
		<symbol id="symbol-brandlogo" viewBox="0 0 276 40">
			{@html dstBrandLogo}
		</symbol>
		<symbol id="symbol-brandicon" viewBox="75 0 42 40">
			<svg xmlns:xlink="http://www.w3.org/1999/xlink" width="276" height="40"
				><use xlink:href="#symbol-brandlogo" /></svg
			>
		</symbol>
		<symbol id="symbol-appicon" viewBox="0 0 28 28">
			{@html dstAppIcon}
		</symbol>
	</svg>

	{#if isLegalVariant}
		<nav id="top" aria-label="Sprungmarken">
			<a class="jumplabel" href="#main">Zum Inhalt</a>
			<!-- prettier-ignore -->
			<a class="jumplabel" on:click|preventDefault|stopPropagation={onJumpLableClick} href="#app-sidebar">Zur Navigation</a>
			<!-- prettier-ignore -->
			<a class="jumplabel" on:click|preventDefault|stopPropagation={onJumpLableClick} href="#app-selectapp" data-closable-target="app-selectapp">Applikation wechseln</a>
			<!-- prettier-ignore -->
			<a class="jumplabel" on:click|preventDefault|stopPropagation={onJumpLableClick} href="#app-useractions" data-closable-target="app-useractions">Zum Usermenü</a>
		</nav>

		<button
			type="button"
			class="app--header--sidebartoggle app--header--sidebartoggle-large"
			title="Menü anzeigen"
			data-closable-target="app-sidebar"
			aria-haspopup="true"
			aria-expanded="false"
		>
			<span>Menü anzeigen</span>
			<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 34 34">
				{#if $$slots.search}
					{@html dstHeaderMenuSearchVariant}
				{:else}
					{@html dstHeaderMenuVariant}
				{/if}
			</svg>
		</button>

		<Sidebar gestalt={gestalt}>
			<slot name="search" slot="search" />
		</Sidebar>
	{/if}

	<div class="app--header--content">
		{#if isLegalVariant}
			<button
				type="button"
				class="app--header--sidebartoggle app--header--sidebartoggle-small"
				on:click|preventDefault|stopPropagation={onSidebarToggleClick}
				title="Menü anzeigen"
				aria-haspopup="true"
				aria-expanded="false"
			>
				<span>Menü anzeigen</span>
				<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
					{#if $$slots.search}
						{@html dstHeaderMenuSearch}
					{:else}
						{@html dstHeaderMenu}
					{/if}
				</svg>
			</button>
		{/if}

		<div class="app--header--selectapp">
			{#if isMinimal}
				<a href="/" class="app--header--branding {brandCssClass}">
					<svg><use xlink:href="#symbol-{brandIcon}" /></svg>
					<span class={brandLabelCssClass}>{brandLabelText}</span>
				</a>
			{:else}
				<a href="{brandUrl}/" class="app--header--branding {brandCssClass}">
					<svg><use xlink:href="#symbol-{brandIcon}" /></svg>
					<span class={brandLabelCssClass}>{brandLabelText}</span>
				</a>
				<a href="{brandUrl}/" class="app--header--branding app--header--branding-compact brandicon">
					<span class="sr-only">Zur Startseite navigieren</span>
					<svg><use xlink:href="#symbol-brandicon" /></svg>
				</a>
				<button
					type="button"
					title="Anwendung wechseln"
					data-closable-target="app-selectapp"
					aria-haspopup="true"
					aria-expanded="false"
				>
					<span>Anwendung wechseln</span>
					<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
						{@html dstHeaderChevronDown}
					</svg>
				</button>
				<dst-flyout
					id="app-selectapp"
					data-closable="app-selectapp"
					data-closable-active="false"
					role="navigation"
					aria-label="Applikation wechseln"
				>
					<nav class="app--header--selectapp--main">
						<div class="sr-only">Applikationen:</div>
						<ul>
							<li>
								<a
									href={appid === 'editorialweb' || appid === 'livecontent' ? baseUrl : 'https://www.derstandard.at'}
									class="appicon appicon--nachrichten"
								>
									<svg><use xlink:href="#symbol-appicon" /></svg><span class="appicon--label">Nachrichten</span></a
								>
							</li>
							<li>
								<a
									href="https://immobilien.derstandard.at/?ref=nav_haupt&utm_source=derstandard&utm_medium=link&utm_campaign=siteguide"
									class="appicon appicon--immobilien"
								>
									<svg><use xlink:href="#symbol-appicon" /></svg><span class="appicon--label">Immobilien</span>
								</a>
							</li>
							<li>
								<a
									href="https://jobs.derstandard.at/?ref=nav_haupt&utm_source=derstandard&utm_medium=link&utm_campaign=siteguide"
									class="appicon appicon--jobs"
								>
									<svg><use xlink:href="#symbol-appicon" /></svg><span class="appicon--label">Jobs</span>
								</a>
							</li>
							<li>
								<a href="https://epaper.derstandard.at" class="appicon appicon--epaper">
									<svg><use xlink:href="#symbol-appicon" /></svg><span class="appicon--label">ePaper</span>
								</a>
							</li>
						</ul>
					</nav>
					<nav class="app--header--selectapp--more">
						<div>Weitere Bereiche:</div>
						<ul>
							<li>
								<a
									href="https://abo.derstandard.at/?utm_source=derstandard&utm_medium=abowidget&utm_campaign=abowidget&utm_content=abowidget&ref=abowidget"
									>Abo &amp; Angebote</a
								>
							</li>
							<li><a href="https://sales.derstandard.at">Sales</a></li>
							<li><a href="https://about.derstandard.at">Unternehmen</a></li>
						</ul>
					</nav>
				</dst-flyout>
			{/if}
		</div>

		<slot name="useractions">
			{#if !$$slots.useractions && !isMinimal}
				<dst-useractions appid={appid} hasnotifications={!$$slots.notifications && !notificationsDisabled} />
			{/if}
		</slot>

		<slot name="notifications">
			{#if !$$slots.notifications && !isMinimal && !notificationsDisabled && $loginState.isLoggedIn}
				<dst-notifications-wrapper hasuseractions={!$$slots.useractions && !isMinimal} />
			{/if}
		</slot>

		{#if !isMinimal}
			<a class="jumplabel" on:click={onJumpLableClick} href="#main" title="Haupt-Navigation überspringen"
				>Haupt-Navigation überspringen</a
			>
		{/if}

		<slot name="navigation">
			{#if !$$slots.navigation}
				<Navigation compact={$isCompactNav} gestalt={gestalt}>
					<slot name="breadcrumb" slot="breadcrumb" />
				</Navigation>
			{/if}
		</slot>

		<svelte:component this={PushNotifications} />

		{#if hasHero}
			<div class="app--header--hero">
				{#if $$slots.hero}
					<slot name="hero" />
				{:else if hasHeroGestalt}
					<dst-promotion />
				{/if}
			</div>
		{/if}

		{#if $$slots.flexzone}
			<slot name="flexzone" />
		{/if}
	</div>
</header>

<style lang="scss" global>
	@use './scss/_index.scss';
</style>
